// Per-event page for Film Clubs listings (Josh 8/13: "it needs to be a per
// event link") — the shareable unit is ONE screening, at a clean URL like
// /film-clubs/aliens-0815 (the popup- row id minus its prefix). Crawlable
// social preview comes from the inject-seo edge function; this page is what
// a tapped link opens. Art Deco language matches FilmClubs.jsx.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import supabase from '../supabase';
import { FILM_CLUB_FILTER, eventUrlPath, clubName, clubUrlPath } from '../utils/filmClubUrls';
import { EVENT_EDITORIAL } from '../utils/popupEditorial';

const GOLD = '#8b6914';
const GOLD_LIGHT = '#d4a843';
const INK = '#2a2520';

export { eventUrlPath };

export default function FilmClubEvent() {
  const { eventId } = useParams();
  const [ev, setEv] = useState(null);
  const [movie, setMovie] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // URL slugs drop the popup- prefix; accept both forms.
        const ids = [`popup-${eventId}`, eventId];
        const { data } = await supabase
          .from('showtimes')
          .select('*')
          .or(FILM_CLUB_FILTER)
          .in('id', ids)
          .limit(1);
        const row = data && data[0];
        if (row) {
          setEv(row);
          const { data: mv } = await supabase
            .from('movies')
            .select('id, title, slug, release_year, poster_path, synopsis')
            .eq('id', String(row.movie_id))
            .limit(1);
          if (mv && mv[0]) setMovie(mv[0]);
        }
      } catch (e) { /* falls through to not-found */ }
      setLoading(false);
    })();
  }, [eventId]);

  const share = async () => {
    const url = window.location.origin + eventUrlPath(ev.id);
    const title = movie && movie.title ? movie.title : ev.cinema_movie_title;
    const club = clubName(ev.series);
    const shareTitle = club ? `${title} — ${club} | Film Revival NYC` : `${title} — Film Revival NYC`;
    if (navigator.share) {
      try { await navigator.share({ title: shareTitle, url }); return; } catch (e) { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { window.prompt('Copy this link:', url); }
  };

  if (loading) {
    return <div style={{ maxWidth: '640px', margin: '0 auto', padding: '40px 16px', textAlign: 'center', color: '#6b6255' }}>Loading…</div>;
  }

  if (!ev) {
    return (
      <div style={{ maxWidth: '640px', margin: '0 auto', padding: '40px 16px', textAlign: 'center', color: INK }}>
        <h1 style={{ fontFamily: 'Cinzel, serif', fontSize: '1.4rem' }}>Event not found</h1>
        <p style={{ color: '#6b6255' }}>This screening may have passed or been removed.</p>
        <a href="/film-clubs" style={{ color: GOLD }}>See all clubs &amp; community screenings →</a>
      </div>
    );
  }

  const start = new Date(ev.start_at);
  const isPast = start < new Date();
  const dateStr = start.toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'long', month: 'long', day: 'numeric' });
  const timeStr = start.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' });
  const title = movie && movie.title
    ? `${movie.title}${movie.release_year ? ` (${movie.release_year})` : ''}`
    : ev.cinema_movie_title;
  const poster = movie && movie.poster_path;

  return (
    <div style={{ maxWidth: '640px', margin: '0 auto', padding: '28px 16px 40px', color: INK }}>
      <a href="/film-clubs" style={{ color: GOLD, textDecoration: 'none', fontSize: '0.85rem' }}>← All clubs &amp; community screenings</a>

      <div style={{ textAlign: 'center', margin: '20px 0 24px' }}>
        <div style={{ fontFamily: 'Cinzel, serif', fontSize: '0.8rem', letterSpacing: '3px', textTransform: 'uppercase', color: GOLD }}>Clubs &amp; Community Screenings</div>
        <div style={{ width: '48px', height: '2px', background: GOLD_LIGHT, margin: '8px auto 0' }} />
      </div>

      <div style={{
        background: '#fdfbf7', border: '1px solid #e5e0d8', borderRadius: '10px',
        boxShadow: '0 3px 12px rgba(42,37,32,0.08)', padding: '22px', textAlign: 'center',
      }}>
        {ev.series && (
          <a href={clubUrlPath(ev.series)} title={`All ${clubName(ev.series)} screenings`} style={{
            fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
            background: 'rgba(212,168,67,0.15)', color: GOLD, border: `1px solid ${GOLD_LIGHT}`,
            borderRadius: '10px', padding: '3px 12px', display: 'inline-block', marginBottom: '14px',
            textDecoration: 'none',
          }}>{ev.series}</a>
        )}
        {poster && (
          <div style={{ margin: '4px 0 16px' }}>
            <img src={poster} alt="" style={{ width: '140px', borderRadius: '6px', boxShadow: '0 2px 10px rgba(42,37,32,0.3)' }} />
          </div>
        )}
        <h1 style={{ fontFamily: 'Cinzel, serif', fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.25, margin: '0 0 12px' }}>
          {movie && movie.slug
            ? <a href={`/films/${movie.slug}`} style={{ color: INK, textDecoration: 'none' }}>{title}</a>
            : title}
        </h1>
        <div style={{ fontSize: '1rem', color: INK, marginBottom: '4px' }}>
          <strong>{dateStr}</strong> · {timeStr}
        </div>
        <div style={{ fontSize: '0.95rem', color: '#6b6255', marginBottom: '18px' }}>{ev.theater}</div>
        {isPast && (
          <p style={{ fontSize: '0.85rem', color: '#a0522d', margin: '0 0 14px' }}>This screening has already happened.</p>
        )}
        {EVENT_EDITORIAL[ev.id] && (
          <div style={{ textAlign: 'left', margin: '0 auto 18px', maxWidth: '460px', borderTop: '1px solid #e5e0d8', paddingTop: '14px' }}>
            {EVENT_EDITORIAL[ev.id].split('\n\n').map((p, i) => (
              <p key={i} style={{ fontSize: '0.88rem', lineHeight: 1.55, color: '#4a443c', margin: '0 0 10px' }}>{p}</p>
            ))}
            <p style={{ fontSize: '0.7rem', color: '#8a8272', margin: 0, fontStyle: 'italic' }}>— program details</p>
          </div>
        )}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {ev.booking_link && !isPast && (
            <a href={ev.booking_link} target="_blank" rel="noopener noreferrer"
              style={{
                display: 'inline-block', padding: '10px 24px', background: GOLD, color: '#fff',
                borderRadius: '5px', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 600,
              }}>
              Tickets & Info
            </a>
          )}
          <button type="button" onClick={share}
            style={{
              padding: '10px 24px', background: 'none', border: `1px solid ${GOLD}`, color: GOLD,
              borderRadius: '5px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
            }}>
            {copied ? 'Link copied!' : 'Share'}
          </button>
        </div>
        {movie && movie.slug && (
          <div style={{ marginTop: '16px' }}>
            <a href={`/films/${movie.slug}`} style={{ color: GOLD, fontSize: '0.85rem' }}>
              All NYC screenings of this film →
            </a>
          </div>
        )}
      </div>

      <div style={{ textAlign: 'center', marginTop: '24px', fontSize: '0.8rem', color: '#8a8272' }}>
        <p style={{ margin: 0 }}>
          Repertory screenings from NYC's film clubs, libraries, and community venues —{' '}
          <a href="/film-clubs" style={{ color: GOLD }}>see the full calendar</a>.
        </p>
      </div>
    </div>
  );
}
